// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { otAssert, toRoundedAmount } from '@shared/conversions'
import { StageConfiguration } from '@shared/DeploymentConfigTypes'
import { ReserveActionPlanStateDataCTO } from '@shared/ReservePlanCTO'

import { LiquidityAssetClass, LiquidityAssetClassTestOutput } from './LiquidityAssetClass'
import { LytReservesClass, LytReservesWithStatsTestOutput } from './LytReservesClass'
import { PlanActionClass, PlanActionClassTestOutput } from './PlanActionClass'

import { logOT } from '@shared/loggingShared'
import { IPoolDynamicWithdrawStage } from '@shared/PoolEventCTO'

const MINIMUM_TRANSACTION_AMOUNT = 10000n // prevent sub -penny withdrawals
export enum ReservePlanModeEnum {
  UnassignedMode = 'Unassigned Mode',
  InflowMode = 'Inflow Mode',
  RetrieveMode = 'Retrieve Mode',
  OutflowMode = 'Outflow Mode',
  NeutralMode = 'Neutral Mode',
}

export interface ReservesPlanTestOutput {
  poolName: string
  reservePlanMode: ReservePlanModeEnum

  preTotalReserves: string
  postTotalReserves: string
  nativeLiquidityAsset: LiquidityAssetClassTestOutput
  reservesWithStats: LytReservesWithStatsTestOutput[]
  planActions: PlanActionClassTestOutput[]
  marketMakerRedeemOutstandingBalance: string
  preTotalPercentReserves: number
  postTotalPercentReserves: number
}
export class ReserveActionPlanClass {
  constructor(params: {
    reserveActionPlanStateDataCTO: ReserveActionPlanStateDataCTO
    lytStageConfiguration: StageConfiguration
    reservePlanMode?: ReservePlanModeEnum
  }) {
    const { reserveActionPlanStateDataCTO, lytStageConfiguration } = params

    const { lytPoolAddr, parentStageConfiguration: otStageConfiguration } = lytStageConfiguration
    otAssert(lytPoolAddr, 'lytPoolAddr is required')
    otAssert(otStageConfiguration, 'otStageConfiguration is required')
    this.timestamp = Date.now()
    this.retrievalAdjustment = 0n
    this.reserveActionPlanStateDataCTO = reserveActionPlanStateDataCTO
    this.lytStageConfiguration = lytStageConfiguration
    this.otStageConfiguration = otStageConfiguration
    this.planActions = []
    const reservesWithStats: LytReservesClass[] = []
    this._adjustmentMin = 0n
    this._adjustmentMax = 0n

    this.nativeLiquidityAsset = new LiquidityAssetClass(reserveActionPlanStateDataCTO.nativeLiquidityAssetState)
    for (const reserve of reserveActionPlanStateDataCTO.reserveAllocationsWithWithdrawalsState) {
      const lytReservesWithStatsClass = new LytReservesClass(reserve, this)
      reservesWithStats.push(lytReservesWithStatsClass)
    }
    this.reservesWithStats = reservesWithStats

    this.defaultPlanMode = this.determineReservePlanMode()
    if (params.reservePlanMode) {
      this.reservePlanMode = params.reservePlanMode
    } else {
      this.reservePlanMode = this.defaultPlanMode
    }

    this.buildFullReserveActionPlan()
  }

  timestamp: number
  retrievalAdjustment: bigint
  reserveActionPlanStateDataCTO: ReserveActionPlanStateDataCTO
  reservesWithStats: LytReservesClass[]
  planActions: PlanActionClass[]
  lytStageConfiguration: StageConfiguration
  otStageConfiguration: StageConfiguration
  nativeLiquidityAsset: LiquidityAssetClass
  _adjustmentMin: bigint
  _adjustmentMax: bigint

  reservePlanMode: ReservePlanModeEnum
  defaultPlanMode: ReservePlanModeEnum

  private listeners: (() => void)[] = []
  /* 
Adjusting reserves
*/
  get adjustingReserve1(): LytReservesClass | undefined {
    const adjustingReserves = this.reservesWithStats.filter((reserve) => reserve.isAdjusting)
    return adjustingReserves.length > 0 ? adjustingReserves[0] : undefined
  }
  get adjustingReserve2(): LytReservesClass | undefined {
    const adjustingReserves = this.reservesWithStats.filter((reserve) => reserve.isAdjusting)
    return adjustingReserves.length > 1 ? adjustingReserves[1] : undefined
  }
  bigintMin(a: bigint, b: bigint): bigint {
    return a < b ? a : b
  }
  get totalAdjustmentValue(): bigint {
    if (this.adjustingReserve2 && this.adjustingReserve1) {
      if (this.reservePlanMode === ReservePlanModeEnum.InflowMode) {
        return this.adjustingReserve1.actualDepositAmount + this.adjustingReserve2.actualDepositAmount
      } else {
        return this.bigintMin(
          this.bigintMin(
            this.adjustingReserve1.actualWithdrawAmount + this.adjustingReserve2.actualWithdrawAmount,
            this.adjustingReserve1.retrievableAssetBalance
          ),
          this.adjustingReserve2.retrievableAssetBalance
        )
      }
    }
    return 0n
  }
  get adjustmentMinValue(): bigint {
    return this._adjustmentMin
  }
  get adjustmentMaxValue(): bigint {
    return this._adjustmentMax
  }

  updateAdjustment(value: number): void {
    otAssert(this.adjustingReserve1 && this.adjustingReserve2, 'adjustingReserve1 and adjustingReserve2 are required')
    const value2 = Number(this.totalAdjustmentValue) - value
    if (this.reservePlanMode === ReservePlanModeEnum.InflowMode) {
      this.adjustingReserve1.actualDepositAmount = BigInt(value)
      this.adjustingReserve2.actualDepositAmount = BigInt(value2)
    }
    if (this.reservePlanMode === ReservePlanModeEnum.RetrieveMode) {
      this.adjustingReserve1.actualWithdrawAmount = BigInt(value)
      this.adjustingReserve2.actualWithdrawAmount = BigInt(value2)
    }
  }

  switchIsAdjusting(isAdjusting: boolean, reserveId: string) {
    const reserve = this.reservesWithStats.find((reserve) => reserve.id === reserveId)
    if (reserve) {
      reserve.isAdjusting = isAdjusting
      reserve.notify()
    }

    if (this.adjustingReserve2 && this.adjustingReserve1) {
      if (this.reservePlanMode === ReservePlanModeEnum.InflowMode) {
        this._adjustmentMin = 0n
      } else {
        this._adjustmentMin = 0n
      }
    } else {
      this._adjustmentMin = 0n
    }

    if (this.adjustingReserve2 && this.adjustingReserve1) {
      this._adjustmentMax = this.totalAdjustmentValue
    } else {
      this._adjustmentMax = 0n
    }
  }

  subscribe(listener: () => void) {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener)
    }
  }
  get reservesPreWithdrawRequestedOrAcceptedAmount(): bigint {
    const _reserveOutstandingWithdrawals =
      this.reserveActionPlanStateDataCTO.reserveAllocationsWithWithdrawalsState.reduce((acc, reserveCTO) => {
        const withdrawRequestedOrAcceptedAmount = reserveCTO.withdrawActivityCTOs.reduce(
          (total, activity) => total + (activity.stage == 1 || activity.stage == 2 ? BigInt(activity.assets) : 0n),
          0n
        )
        return acc + withdrawRequestedOrAcceptedAmount
      }, 0n)
    return _reserveOutstandingWithdrawals
  }

  get marketMakerRedeemOutstandingBalance(): bigint {
    const _marketMakerRedeemOutstandingBalance = this.reserveActionPlanStateDataCTO.lytWithdrawActivityCTOs.reduce(
      (total, activity) => total + (activity.stage == 1 || activity.stage == 2 ? BigInt(activity.assets) : 0n),
      0n
    )
    return _marketMakerRedeemOutstandingBalance
  }
  get preTotalReserves(): bigint {
    let total = this.reserveActionPlanStateDataCTO.reserveAllocationsWithWithdrawalsState.reduce((acc, reserveCTO) => {
      // const withdrawRequestedOrAcceptedAmount = reserveCTO.withdrawActivityCTOs.reduce(
      //   (total, activity) => total + (activity.stage == 1 || activity.stage == 2 ? BigInt(activity.assets) : 0n),
      //   0n
      // )
      // return acc + BigInt(reserveCTO.currentAssetValue) + BigInt(withdrawRequestedOrAcceptedAmount)
      return acc + BigInt(reserveCTO.currentAssetValue ?? reserveCTO.assetBalance)
    }, 0n)
    total = total + BigInt(this.reserveActionPlanStateDataCTO.nativeLiquidityAssetState.lytPoolBalance)

    return total
  }

  get postTotalReserves(): bigint {
    let total = this.reserveActionPlanStateDataCTO.reserveAllocationsWithWithdrawalsState.reduce((acc, reserveCTO) => {
      const withdrawRequestedOrAcceptedAmount = reserveCTO.withdrawActivityCTOs.reduce(
        (total, activity) => total + (activity.stage == 1 || activity.stage == 2 ? BigInt(activity.assets) : 0n),
        0n
      )

      return (
        acc +
        BigInt(reserveCTO.currentAssetValue ?? reserveCTO.assetBalance) -
        BigInt(withdrawRequestedOrAcceptedAmount)
      )
    }, 0n)

    total = total + BigInt(this.reserveActionPlanStateDataCTO.nativeLiquidityAssetState.lytPoolBalance)

    total = total + this.reservesPreWithdrawRequestedOrAcceptedAmount - this.marketMakerRedeemOutstandingBalance
    return total
  }

  validate() {
    for (const reserve of this.reservesWithStats) {
      reserve.validate()
    }
    for (const planAction of this.planActions) {
      planAction.validate()
    }

    const sumOfPercentReserves = this.reservesWithStats.reduce((acc, reserve) => acc + reserve.postPercentReserves, 0)
    if (this.postTotalReserves > 0n && (sumOfPercentReserves < 99.5 || sumOfPercentReserves > 100.5)) {
      throw new Error(`Sum of percent reserves is not 100  ${sumOfPercentReserves} ${this.postTotalReserves}`)
    }
    const sumOfAssets = this.planActions.reduce((acc, planAction) => acc + planAction.actualAssets, 0n)

    if (this.reservePlanMode === ReservePlanModeEnum.InflowMode) {
      if (
        sumOfAssets + 100n < this.liquidityAssetReserve.actualDepositAmount ||
        sumOfAssets - 100n > this.liquidityAssetReserve.actualDepositAmount
      ) {
        throw new Error(
          `Sum of assets is not equal to total reserves ${sumOfAssets} ${this.liquidityAssetReserve.actualDepositAmount}`
        )
      }
    }

    this.notify()
  }
  notify() {
    this.listeners.forEach((listener) => listener())
  }

  get liquidityAssetReserve(): LytReservesClass {
    const _liquidityAssetReserve = this.reservesWithStats.find((reserve) => reserve.isLiquidityAsset)
    otAssert(_liquidityAssetReserve, 'liquidityAssetReserve not found')
    return _liquidityAssetReserve
  }

  get symbol(): string {
    return this.reserveActionPlanStateDataCTO.lytPoolOverview.symbol
  }
  get poolName(): string {
    return (
      this.reserveActionPlanStateDataCTO.lytPoolOverview.displayName ??
      this.reserveActionPlanStateDataCTO.lytPoolOverview.name
    )
  }

  get preTotalPercentReserves(): number {
    return this.reservesWithStats.reduce((acc, reserve) => acc + reserve.prePercentReserves, 0)
  }
  get postTotalPercentReserves(): number {
    return this.reservesWithStats.reduce((acc, reserve) => acc + reserve.postPercentReserves, 0)
  }
  get testOutput(): ReservesPlanTestOutput {
    return {
      poolName: this.poolName,
      reservePlanMode: this.reservePlanMode,

      preTotalReserves: toRoundedAmount(this.preTotalReserves),
      postTotalReserves: toRoundedAmount(this.postTotalReserves),
      nativeLiquidityAsset: this.nativeLiquidityAsset.testOutput,
      reservesWithStats: this.reservesWithStats.map((reserve) => reserve.testOutput),
      planActions: this.planActions.map((planAction) => planAction.testOutput),
      marketMakerRedeemOutstandingBalance: toRoundedAmount(this.marketMakerRedeemOutstandingBalance),
      preTotalPercentReserves: Math.round(this.preTotalPercentReserves),
      postTotalPercentReserves: Math.round(this.postTotalPercentReserves),
    }
  }

  toString(): string {
    const structuredOutput = this.testOutput
    return JSON.stringify(structuredOutput, null, 2)
  }
  [Symbol.for('nodejs.util.inspect.custom')](): string {
    return this.toString()
  }

  private determineReservePlanMode(): ReservePlanModeEnum {
    let reservePlanMode = ReservePlanModeEnum.UnassignedMode
    const fundsAvailableToRepay =
      this.nativeLiquidityAsset.lytPoolBalance + this.nativeLiquidityAsset.lytIssuerVaultBalance

    if (this.reserveActionPlanStateDataCTO.lytWithdrawActivityCTOs.length > 0) {
      for (const lytWithdrawActivityCTO of this.reserveActionPlanStateDataCTO.lytWithdrawActivityCTOs) {
        if (
          lytWithdrawActivityCTO.stage === IPoolDynamicWithdrawStage.Requested ||
          lytWithdrawActivityCTO.stage === IPoolDynamicWithdrawStage.Accepted
        ) {
          if (fundsAvailableToRepay >= BigInt(lytWithdrawActivityCTO.assets)) {
            reservePlanMode = ReservePlanModeEnum.OutflowMode
            return reservePlanMode
          }
        }
      }

      if (reservePlanMode === ReservePlanModeEnum.UnassignedMode) {
        for (const lytWithdrawActivityCTO of this.reserveActionPlanStateDataCTO.lytWithdrawActivityCTOs) {
          if (
            lytWithdrawActivityCTO.stage === IPoolDynamicWithdrawStage.Requested ||
            lytWithdrawActivityCTO.stage === IPoolDynamicWithdrawStage.Accepted
          ) {
            reservePlanMode = ReservePlanModeEnum.RetrieveMode
            return reservePlanMode
          }
        }
      }
    }
    for (const lytReservesWithStatsClass of this.reservesWithStats) {
      if (lytReservesWithStatsClass.isLiquidityAsset) {
        if (
          reservePlanMode === ReservePlanModeEnum.UnassignedMode &&
          lytReservesWithStatsClass.preAssetBalance > lytReservesWithStatsClass.maxBalanceAmount
        ) {
          reservePlanMode = ReservePlanModeEnum.InflowMode
          return reservePlanMode
        }
      }
    }

    if (reservePlanMode === ReservePlanModeEnum.UnassignedMode) {
      reservePlanMode = ReservePlanModeEnum.NeutralMode
    }
    return reservePlanMode
  }
  private buildFullReserveActionPlan() {
    if (this.reservePlanMode === ReservePlanModeEnum.InflowMode) {
      this.buildReserveDepositActions()
    } else if (this.reservePlanMode === ReservePlanModeEnum.RetrieveMode) {
      this.buildReserveRedeemRequestActions()
    } else if (this.reservePlanMode === ReservePlanModeEnum.OutflowMode) {
      this.buildMarketMakerRedeemActions()
    } else {
      this.buildReserveNeutralActions()
    }
  }

  private buildReserveNeutralActions(): void {
    this.planActions = []
  }
  private buildReserveDepositActions(): void {
    logOT.BANNER(this.buildReserveDepositActions.name, 'START')
    const { reservesWithStats, postTotalReserves } = this

    const totalPreAssetBalance = reservesWithStats.reduce((acc, curr) => acc + curr.preAssetBalance, 0n)

    logOT.info(this.buildReserveDepositActions.name, { postTotalReserves, totalPreAssetBalance })

    let remainingBalance = this.liquidityAssetReserve.preAssetBalance

    const sortedReserves = [...reservesWithStats].sort((a, b) => a.depositPriority - b.depositPriority)

    // First we deposit the minimum amount of each reserve
    sortedReserves.forEach((reserve) => {
      logOT.info(this.buildReserveDepositActions.name, {
        tag: 'MIN LOOP',
        assetBalanceForDepositCalculation: reserve.assetBalanceForDepositCalculation,
        remainingBalance,
        minBalanceAmount: reserve.minBalanceAmount,
        reserve,
      })

      if (
        reserve.assetBalanceForDepositCalculation < reserve.minBalanceAmount &&
        remainingBalance > MINIMUM_TRANSACTION_AMOUNT
      ) {
        const depositAmount = reserve.minBalanceAmount - reserve.assetBalanceForDepositCalculation
        if (depositAmount < remainingBalance) {
          reserve.depositNeededForMin = depositAmount
          reserve.actualDepositAmount = depositAmount
          reserve.defaultDepositAmount = depositAmount
          remainingBalance = remainingBalance - depositAmount
          logOT.info(this.buildReserveDepositActions.name, {
            tag: ' MinDeposit 1.2 depositAmount > remainingBalance',
            poolName: reserve.poolName,
            depositAmount,
            remainingBalance,
          })
        } else {
          reserve.depositNeededForMin = remainingBalance
          reserve.actualDepositAmount = remainingBalance
          reserve.defaultDepositAmount = remainingBalance
          remainingBalance = 0n
          logOT.info(this.buildReserveDepositActions.name, {
            tag: 'MinDeposit 2.2 depositAmount < remainingBalance',
            poolName: reserve.poolName,
            depositAmount,
            remainingBalance,
          })
        }
      }
    })
    // RemainingToMaxDeposit is the remaining balance after the minimum deposits
    logOT.info(' remainingBalance After MIN deposits', toRoundedAmount(remainingBalance))
    sortedReserves.forEach((reserve) => {
      if (remainingBalance > MINIMUM_TRANSACTION_AMOUNT) {
        const amountToMaxDeposit =
          reserve.maxBalanceAmount - (reserve.assetBalanceForDepositCalculation + reserve.depositNeededForMin)
        logOT.info(this.buildReserveDepositActions.name, {
          tag: ' RemainingToMaxDeposit amountToMaxDeposit',
          poolName: reserve.poolName,
          amountToMaxDeposit,
        })
        if (remainingBalance < amountToMaxDeposit) {
          reserve.actualDepositAmount = reserve.depositNeededForMin + remainingBalance
          reserve.defaultDepositAmount = reserve.depositNeededForMin + remainingBalance
          remainingBalance = 0n
          logOT.info(this.buildReserveDepositActions.name, {
            tag: ' RemainingToMaxDeposit 1.2 remainingBalance < amountToMaxDeposit',
            poolName: reserve.poolName,
            remainingBalance,
          })
        } else {
          reserve.actualDepositAmount = reserve.depositNeededForMin + amountToMaxDeposit
          reserve.defaultDepositAmount = reserve.depositNeededForMin + amountToMaxDeposit
          remainingBalance = remainingBalance - amountToMaxDeposit
          logOT.info(this.buildReserveDepositActions.name, {
            tag: ' RemainingToMaxDeposit 2.2 remainingBalance < amountToMaxDeposit',
            poolName: reserve.poolName,
            remainingBalance,
          })
        }
      }
    })
    logOT.BANNER(this.buildReserveDepositActions.name, '3', 'Create Plan Actions', sortedReserves)

    sortedReserves.forEach((reserve) => {
      logOT.info(
        this.buildReserveDepositActions.name,
        ' defaultDepositAmount',
        reserve.poolName,
        reserve.defaultDepositAmount
      )

      //    if (reserve.defaultDepositAmount > 0n) {
      const planAction = new PlanActionClass({
        reserveId: reserve.id,
        reserveActionPlanClass: this,
      })

      this.planActions.push(planAction)
    })

    logOT.BANNER(this.buildReserveDepositActions.name, {
      tag: 'Created PreserveActionPlan.planActions ',
      planActions: this.planActions,
    })

    logOT.BANNER(this.buildReserveDepositActions.name, ' SUCCESS')
  }

  private buildReserveRedeemRequestActions(): void {
    logOT.BANNER(this.buildReserveRedeemRequestActions.name, 'START')
    const nativeLiquidityAsset = this.nativeLiquidityAsset
    otAssert(nativeLiquidityAsset, 'nativeLiquidityAsset is required')
    const lytPoolBalance = nativeLiquidityAsset.lytPoolBalance

    const { reservesWithStats } = this

    const totalAssetBalance = reservesWithStats.reduce((acc, curr) => acc + BigInt(curr.preAssetBalance), 0n)

    let totalReserveBalanceAfterWithdraws =
      totalAssetBalance - this.marketMakerRedeemOutstandingBalance - this.reservesPreWithdrawRequestedOrAcceptedAmount

    if (totalReserveBalanceAfterWithdraws < 0n) {
      totalReserveBalanceAfterWithdraws = 0n
    }
    const sortedReserves = [...reservesWithStats].sort((a, b) => a.withdrawPriority - b.withdrawPriority)

    let remainingWithdrawAmount =
      this.marketMakerRedeemOutstandingBalance - lytPoolBalance - this.reservesPreWithdrawRequestedOrAcceptedAmount

    logOT.info(this.buildReserveRedeemRequestActions.name, {
      tag: 'LOOP START',
      remainingWithdrawAmount,
      lytPoolBalance,
      marketMakerRedeemOutstandingBalance: this.marketMakerRedeemOutstandingBalance,
    })
    sortedReserves.forEach((reserve, index) => {
      reserve.actualWithdrawAmount = 0n
      const netAssetBalance = BigInt(reserve.preAssetBalance) - BigInt(reserve.preWithdrawRequestedOrAcceptedAmount)

      if (netAssetBalance > reserve.maxBalanceAmount && remainingWithdrawAmount > 0n) {
        const withdrawAmount = netAssetBalance - reserve.maxBalanceAmount - reserve.preWithdrawRequestedOrAcceptedAmount

        logOT.info(this.buildReserveRedeemRequestActions.name, ` LOOP 1 `, {
          index,
          withdrawAmount,
          maxBalanceAmount: reserve.maxBalanceAmount,
          preWithdrawRequestedOrAcceptedAmount: reserve.preWithdrawRequestedOrAcceptedAmount,
        })

        if (withdrawAmount > remainingWithdrawAmount) {
          logOT.info(this.buildReserveRedeemRequestActions.name, ' 1 withdrawAmount > remainingBalance', {
            withdrawAmount,
            remainingWithdrawAmount,
          })
          reserve.actualWithdrawAmount = remainingWithdrawAmount
          reserve.defaultWithdrawAmount = remainingWithdrawAmount
          remainingWithdrawAmount = 0n
        } else {
          logOT.info(this.buildReserveRedeemRequestActions.name, '1. Remaining withdrawAmount < remainingBalance', {
            withdrawAmount,
            remainingWithdrawAmount,
          })
          reserve.actualWithdrawAmount = withdrawAmount
          reserve.defaultWithdrawAmount = withdrawAmount
          remainingWithdrawAmount = remainingWithdrawAmount - withdrawAmount
        }
      }
    })

    logOT.info(this.buildReserveRedeemRequestActions.name, ' After MIN withdraws', {
      remainingWithdrawAmount: toRoundedAmount(remainingWithdrawAmount),
    })
    sortedReserves.forEach((reserve) => {
      const assetBalance = reserve.preAssetBalance - reserve.preWithdrawRequestedOrAcceptedAmount

      if (remainingWithdrawAmount > MINIMUM_TRANSACTION_AMOUNT) {
        const amountToMinWithdraw = assetBalance - reserve.minBalanceAmount - reserve.actualWithdrawAmount

        if (remainingWithdrawAmount > amountToMinWithdraw) {
          logOT.info(this.buildReserveRedeemRequestActions.name, '2  amountToMinWithdraw < remainingBalance', {
            amountToMinWithdraw,
            remainingWithdrawAmount,
          })

          reserve.actualWithdrawAmount = reserve.actualWithdrawAmount + amountToMinWithdraw
          reserve.defaultWithdrawAmount = reserve.defaultWithdrawAmount + amountToMinWithdraw
          remainingWithdrawAmount = remainingWithdrawAmount - amountToMinWithdraw
        } else {
          logOT.info(this.buildReserveRedeemRequestActions.name, '2 REMAINING', {
            amountToMinWithdraw,
            remainingWithdrawAmount,
          })

          reserve.actualWithdrawAmount = reserve.actualWithdrawAmount + remainingWithdrawAmount
          reserve.defaultWithdrawAmount = reserve.defaultWithdrawAmount + remainingWithdrawAmount
          remainingWithdrawAmount = 0n
        }
      }
    })
    logOT.BANNER(this.buildReserveRedeemRequestActions.name, ' After MIN withdraws', {
      remainingWithdrawAmount: toRoundedAmount(remainingWithdrawAmount),
    })

    sortedReserves.forEach((reserve) => {
      //    if (reserve.defaultDepositAmount > 0n) {
      const planAction = new PlanActionClass({
        reserveId: reserve.id,
        reserveActionPlanClass: this,
      })

      this.planActions.push(planAction)
    })

    logOT.BANNER(this.buildReserveRedeemRequestActions.name, ' SUCCESS')
  }

  private buildMarketMakerRedeemActions(): void {
    const planActions: PlanActionClass[] = []

    const transferAmount = this.reserveActionPlanStateDataCTO.lytWithdrawActivityCTOs.reduce(
      (acc, activity) => acc + BigInt(activity.assets),
      0n
    )

    const planAction = new PlanActionClass({
      transferAmount,
      reserveActionPlanClass: this,
    })
    planActions.push(planAction)

    this.reserveActionPlanStateDataCTO.lytWithdrawActivityCTOs?.forEach((dynamicWithdrawalActivityCTO) => {
      const planAction = new PlanActionClass({
        dynamicWithdrawalActivityCTO,
        reserveActionPlanClass: this,
      })
      planActions.push(planAction)
    })
    this.planActions = planActions
  }
}
